/**
 * Beschriftung und Erklärung der Antragsfelder.
 *
 * Dieselben Felder erscheinen an zwei Stellen: im Anlegeformular (`VerfahrenFormular`) und in
 * den Stammdaten eines geöffneten Antrags (`AntragStammdaten`), beide über `AntragFelder`. Die
 * Texte stehen deshalb genau einmal hier – doppelt gepflegt liefen die Erklärungen unweigerlich
 * auseinander.
 */
import { UvpStatusOption } from "../../../../types";
import type { AntragFeld } from "./AntragFelder";

export interface Feldtext {
  label: string;
  tooltip: string;
}

export const ANTRAG_FELDER: Record<AntragFeld, Feldtext> = {
  bezeichnung: {
    label: "Bezeichnung",
    tooltip:
      "Freier Name zur Unterscheidung mehrerer Anträge desselben Projekts. Hat keine Auswirkung auf die benötigten Antragsbausteine.",
  },
  beschreibung: {
    label: "Beschreibung",
    tooltip:
      "Optionaler Freitext zum Antrag. Rein informativ, geht in keine Auswertung ein.",
  },
  vorhabenbestandteilIds: {
    label: "Umfang",
    tooltip:
      "Was neben den Windenergieanlagen zum Antrag gehört, z. B. Umspannwerk oder Batteriespeicher. Steht im Antrag bei den Anlagenteilen und Nebeneinrichtungen (ELiA 1.1.2). Welche Antragsbausteine dafür nötig sind, legt die Auswahl nicht fest – die ordnen Sie selbst zu.",
  },
  anlageIds: {
    label: "Anlagen",
    tooltip:
      "Die Windenergieanlagen, die dieser Antrag umfasst. Ein Neuantrag umfasst in der Regel den ganzen Park, ein Änderungsantrag oder Typenwechsel oft nur einzelne Anlagen. Die Formulare des Antrags füllen sich nur mit diesen Anlagen. Ohne Anlage ist der Antrag unvollständig; eine gelöschte Anlage fällt aus dem Antrag heraus.",
  },
  procedureTypeId: {
    label: "Antragsart",
    tooltip:
      "Was mit diesem Antrag beantragt wird – entspricht der Leistung in ELiA und enthält bei der Neugenehmigung zugleich die Verfahrensart (§ 10 förmlich mit Öffentlichkeitsbeteiligung ab 20 Anlagen, § 19 vereinfacht darunter, Nr. 1.6 der 4. BImSchV). Bestimmt maßgeblich, welche Antragsbausteine der Antrag braucht.",
  },
  uvpStatusId: {
    label: "UVP-Status",
    tooltip:
      // Die Schwellen der Vorbelegung stehen in regeln/vorbelegung_antrag.py – ändern sie sich
      // dort, ist dieser Text mitzuziehen.
      "Stand der Umweltverträglichkeitsprüfung. Bestimmt, ob UVP-Bericht bzw. Vorprüfungsunterlagen einzureichen sind. Vorbelegt nach der Zahl der gewählten Anlagen (Anlage 1 UVPG, Nr. 1.6): ab 20 Anlagen UVP zwingend erforderlich, ab 3 Vorprüfung erforderlich, darunter keine UVP. Die Vorbelegung zieht mit der Anlagenauswahl mit, bis Sie den Status selbst wählen – sie ist eine Prognose, kein amtliches Ergebnis.",
  },
  bezugsgenehmigungId: {
    label: "Bezugsantrag",
    tooltip:
      "Der Antrag in strømcraft, auf den dieser aufsetzt – bei Änderung, Repowering und Teilgenehmigung der Regelfall. Zunächst stehen die Anträge dieses Projekts zur Wahl; ein Repowering bezieht sich aber oft auf den Altpark in einem anderen Projekt, deshalb lassen sich alle Anträge der Organisation einblenden.",
  },
  zusatzantragBemerkung: {
    label: "Bemerkung zum Zusatzantrag",
    tooltip:
      "Freitext zum Zusatzantrag, der mit diesem Antrag gestellt wird. ELiA führt in Formular 1.1.3 neben jedem Kreuz ein Bemerkungsfeld; das Kreuz folgt bei uns aus der Antragsart, die Bemerkung aus diesem Feld. Erscheint nur bei Teilgenehmigung und vorzeitigem Beginn – nur dort setzt die Antragsart ein Kreuz.",
  },
  bearbeitungUserId: {
    label: "Bearbeitung",
    tooltip:
      "Wer den Antrag bei uns bearbeitet. Die Wiedervorlagen des Antrags – ausstehende Vollständigkeitsmitteilung und ablaufende Entscheidungsfrist – erscheinen in der Terminliste dieser Person. Beim Anlegen ist der eigene Benutzer vorbelegt.",
  },
  eingereichtAm: {
    label: "Eingereicht am",
    tooltip:
      "Eingang des Antrags bei der Behörde – nicht der Tag, an dem der Stand hier gepflegt wurde. Ab diesem Datum läuft die Monatsfrist, in der die Behörde die Unterlagen auf Vollständigkeit prüft (§ 7 Abs. 1 der 9. BImSchV), und daraus entsteht die Wiedervorlage.",
  },
  vollstaendigSeit: {
    label: "Vollständig seit",
    tooltip:
      "Datum der Mitteilung, dass die Unterlagen vollständig sind. Ab hier läuft die Entscheidungsfrist: sieben Monate im förmlichen Verfahren mit Öffentlichkeitsbeteiligung, sonst drei (§ 10 Abs. 6a BImSchG).",
  },
  aktenzeichen: {
    label: "Aktenzeichen",
    tooltip:
      "Das behördliche Aktenzeichen dieses Antrags. Die Genehmigungsbehörde vergibt es mit dem Eingang des Antrags, oft schon bei der Antragskonferenz. Es steht später auf dem Bescheid und ist das, worauf ein Folgeantrag Bezug nimmt.",
  },
  genehmigungGeplantAm: {
    label: "Geplante Genehmigung",
    tooltip:
      "Bis wann die Genehmigung erwartet wird. Rechte Grenze des Zeitstrahls auf der Seite „Nachweise“ – Termine danach werden dort rot.",
  },
  conferencePlannedOn: {
    label: "Antragskonferenz am",
    tooltip:
      "Der geplante Termin der Antragskonferenz mit Genehmigungs- und Fachbehörden, in der Umfang und geforderte Gutachten abgestimmt werden. Freiwillig. Der Antrag beginnt im Status „Konferenz ausstehend“; der Termin erscheint in den Terminen.",
  },
  bescheidVom: {
    label: "Bescheid vom",
    tooltip:
      "Datum des Genehmigungsbescheids, also der Entscheidung der Behörde. Nicht das Datum der Bestandskraft – die tritt erst mit Ablauf der Rechtsbehelfsfrist ein.",
  },
  windenergiegebietId: {
    label: "Windenergiegebiet",
    tooltip:
      "Ob und wie die Fläche in einem ausgewiesenen Windenergiegebiet liegt. Wirkt sich auf standortbezogene Anforderungen aus, z. B. im Beschleunigungsgebiet.",
  },
  standortmerkmalIds: {
    label: "Besonderheiten des Standorts",
    tooltip:
      "Weitere, frei kombinierbare Eigenschaften des Standorts, z. B. Schutzgebiet oder Wald. Kann zusätzliche Nachweise auslösen, ist aber nicht verpflichtend.",
  },
};

/**
 * Auswahl des UVP-Status mit Gruppenüberschriften („UVP-Pflicht“, „Ergebnis der Vorprüfung“).
 * Die Gruppe kommt vom Server, die Reihenfolge ebenso.
 */
export const uvpOptionen = (optionen: UvpStatusOption[]) =>
  optionen.map((option) => ({
    value: option.id,
    label: option.name,
    groupLabel: option.gruppe ?? undefined,
  }));
